import React, { useCallback, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Dropdown from 'components/Luxkit/Dropdown'
import useToggle from 'hooks/useToggle'
import { pluralizeToString } from 'lib/string/pluralize'
import FormatCurrency from 'components/Common/FormatCurrency'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import List from 'components/Common/List'
import { formatRoomType } from 'lib/tours/tourUtils'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import Caption from 'components/Luxkit/Typography/Caption'

const DropdownContainer = styled.div`
  position: relative;
  display: inline;
`

const BreakdownSection = styled.li`
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(4)};
  }
`

const BreakdownSectionsContainer = styled(List)`
  display: flex;
  flex-direction: column;

  > ${BreakdownSection} + ${BreakdownSection} {
    margin-top: ${rem(4)};
  }

  &.has-dividers > ${BreakdownSection} + ${BreakdownSection} {
    margin-top: unset;

    &::before {
      border-top: 1px solid ${({ theme }) => theme.palette.neutral.six};
      content: "";
      margin: ${rem(12)} 0;
      width: 100%;
    }
  }
`

const BreakdownItem = styled(BodyText)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const BreakdownTotal = styled(Heading)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledDropdown = styled(Dropdown)`
  background-color: ${props => props.theme.palette.neutral.eight};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  padding: ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.M};;
  width: ${rem(256)};
  cursor: default;
`

const SystemMessaging = styled.div`
  display: flex;
  padding: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${({ theme }) => theme.palette.neutral.seven};
  border: 1px solid ${({ theme }) => theme.palette.neutral.six};

  > p {
    margin: 0;
  }

  > * + p {
    margin-left: ${rem(8)};
  }
`

interface Props {
  purchasableOptionsPriceBreakdowns: [Tours.V2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown][]
  totalPrice: number
  isLeTour: boolean
}

function PricingBreakdownDropdown(props: Props) {
  const {
    purchasableOptionsPriceBreakdowns,
    totalPrice,
    isLeTour,
  } = props

  const triggerRef = useRef<HTMLButtonElement>()
  const [isDropdownOpen, toggleDropdown, , closeDropdown] = useToggle()

  const doNotBubble = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleToggleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    doNotBubble(event)
    toggleDropdown()
  }, [toggleDropdown, doNotBubble])

  return (
    <DropdownContainer>
      <SmallToggleButton
        on={isDropdownOpen}
        onClick={handleToggleClick}
        ref={triggerRef}
        size="small"
      >
        Price details
      </SmallToggleButton>
      <StyledDropdown
        alignment="left"
        position="below"
        caretType="none"
        open={isDropdownOpen}
        title="Price details"
        triggerRef={triggerRef}
        onClose={closeDropdown}
        onClick={doNotBubble}
      >
        <BreakdownSectionsContainer
          forwardedAs="ul"
          type="unstyled"
          className="has-dividers"
        >
          {purchasableOptionsPriceBreakdowns.map(
            ([purchasableOption, priceBreakdown], index) => (
              <BreakdownSection key={`breakdown-${index}`}>
                <BreakdownItem
                  variant="medium"
                  weight="semi-bold"
                  forwardedAs="span"
                >
                  <span>{formatRoomType(purchasableOption.roomType)}</span>
                  <FormatCurrency value={priceBreakdown.totalPrice} />
                </BreakdownItem>
                <BreakdownSectionsContainer forwardedAs="ul" type="unstyled">
                  {priceBreakdown.adultsPriceBreakdown.map(
                    (adultPrice, adultPriceIndex) => (
                      <BreakdownSection key={`adult-${adultPriceIndex}`}>
                        <BreakdownItem variant="medium" forwardedAs="span">
                          <span>{isLeTour ? 'Traveller' : 'Adult'} {adultPriceIndex + 1}</span>
                          <FormatCurrency value={adultPrice} />
                        </BreakdownItem>
                      </BreakdownSection>
                    ),
                  )}
                  {priceBreakdown.childrenPriceBreakdown.map(
                    (childPrice, childPriceIndex) => (
                      <BreakdownSection key={`child-${childPriceIndex}`}>
                        <BreakdownItem variant="medium" forwardedAs="span">
                          <span>Child</span>
                          <FormatCurrency value={childPrice} />
                        </BreakdownItem>
                      </BreakdownSection>
                    ),
                  )}
                </BreakdownSectionsContainer>
                {priceBreakdown.childrenPriceBreakdown.length >
                  priceBreakdown.appliedChildDiscounts && (
                    <SystemMessaging>
                      <div>
                        <LineInfoCircleIcon size="S" />
                      </div>
                      <Caption variant="medium" as="p">
                        Child discount applies to{' '}
                        {pluralizeToString(
                          'child',
                          priceBreakdown.appliedChildDiscounts,
                        )}{' '}
                        only, remainder will be charged as an adult.
                      </Caption>
                    </SystemMessaging>
                )}
              </BreakdownSection>
            ),
          )}
          <BreakdownSection>
            <BreakdownTotal variant="heading6">
              <div>Total</div>
              <FormatCurrency value={totalPrice} />
            </BreakdownTotal>
          </BreakdownSection>
        </BreakdownSectionsContainer>
      </StyledDropdown>
    </DropdownContainer>
  )
}

export default PricingBreakdownDropdown
